import React, { FC, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../common/constants/constants';

/**
 * Shown over the recorder when the camera or microphone cannot be used,
 * instead of a black screen. Copy depends on why: blocked/dismissed permission,
 * blocked by the device's own privacy settings, camera busy, or no camera. Two ways out: try again (re-requests the camera, useful
 * after the person changes the setting) or go back to the ticket and choose a
 * video from the library.
 */

export type CameraProblem = 'blocked' | 'system' | 'in_use' | 'not_found' | 'other';
/** Which device is refused. Recording asks for camera and microphone in one
 *  request, so this comes from the permission states after a refusal. */
export type BlockedDevice = 'camera' | 'microphone' | 'both';

export const cameraProblemFromError = (error: string | undefined | null): CameraProblem | null => {
  if (!error || error === 'NONE') return null;
  if (error === 'permission_denied') return 'blocked';
  if (error === 'media_in_use') return 'in_use';
  if (error === 'no_specified_media_found') return 'not_found';
  return 'other';
};

const platformHint = (): string => {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  // Android first: an iPadOS check by touch points also matches desktops
  // with touchscreens or device emulation.
  if (/Android/.test(ua)) {
    return 'On Android: tap the icon left of the address, then Permissions, and allow Camera and Microphone.';
  }
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS)
    return 'On iPhone: tap “aA” in the address bar, then Website Settings, and set Camera and Microphone to Allow.';
  return 'Click the camera icon in the address bar, allow camera and microphone, then try again.';
};

/** The site is allowed but the operating system blocks the browser. */
const systemHint = (): string => {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  if (/Android/.test(ua)) {
    return 'On Android: open Settings, then Apps, your browser, Permissions, and allow Camera and Microphone.';
  }
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'On iPhone: open Settings, find your browser, and allow Camera and Microphone.';
  if (/Windows/.test(ua)) {
    return 'On Windows: Settings, Privacy & security, then Camera and Microphone: allow your browser.';
  }
  return 'On a Mac: System Settings, Privacy & Security, then Camera and Microphone: turn on your browser.';
};

const BLOCKED_TITLE: Record<BlockedDevice, string> = {
  both: 'Camera and microphone are off',
  camera: 'Camera access is off',
  microphone: 'Microphone access is off',
};

const COPY: Record<CameraProblem, { title: string; body: string }> = {
  blocked: {
    title: BLOCKED_TITLE.both,
    body: 'Recording needs both your camera and your microphone.',
  },
  system: {
    title: 'Your device is blocking the camera',
    body: 'This site is allowed, but your device is not letting the browser use the camera or microphone.',
  },
  in_use: {
    title: 'Your camera or microphone is busy',
    body: 'Another app or tab is using them. Close it, then try again.',
  },
  not_found: {
    title: 'No camera or microphone found',
    body: 'Recording needs both. You can still choose a video you already have.',
  },
  other: {
    title: 'Recording could not start',
    body: 'Something stopped the camera or microphone from starting. Try again, or choose a video you already have.',
  },
};

const CameraOffIcon = (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
    <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
    <path d="M15.5 10l6-3v10l-6-3z" strokeLinejoin="round" />
    <path d="M3 3l18 18" strokeLinecap="round" />
  </svg>
);

const MicOffIcon = (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21M8.5 21h7" strokeLinecap="round" />
    <path d="M3 3l18 18" strokeLinecap="round" />
  </svg>
);

const Panel = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 40,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '14px',
  padding: '24px',
  textAlign: 'center',
  background: '#111214',
  color: '#fff',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
});

const Title = styled('h2')({ margin: 0, fontSize: '20px', fontWeight: 600, lineHeight: 1.25 });
const Body = styled('p')({
  margin: 0,
  fontSize: '15px',
  lineHeight: 1.45,
  color: 'rgba(255,255,255,.82)',
  maxWidth: '32ch',
});
const Hint = styled('p')({
  margin: 0,
  fontSize: '13.5px',
  lineHeight: 1.45,
  color: 'rgba(255,255,255,.62)',
  maxWidth: '34ch',
});

const buttonBase = {
  appearance: 'none' as const,
  width: '100%',
  maxWidth: '280px',
  minHeight: '48px',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:focus-visible': { outline: '3px solid #fff', outlineOffset: '2px' },
};
// Brand color, like the ticket's primary actions.
const Primary = styled('button')(({ theme }) => ({
  ...buttonBase,
  border: 0,
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginTop: '8px',
}));
const Secondary = styled('button')({
  ...buttonBase,
  border: '1px solid rgba(255,255,255,.4)',
  background: 'transparent',
  color: '#fff',
});

interface Props {
  problem: CameraProblem;
  /** For a refusal: which device. Ignored for other problems. */
  device?: BlockedDevice;
  onRetry: () => void;
  onBack: () => void;
}

export const CameraBlockedPanel: FC<Props> = ({ problem, device = 'both', onRetry, onBack }) => {
  const { trackEvent } = useTrackEvent();
  const copy = COPY[problem];
  const hint = problem === 'blocked' ? platformHint() : problem === 'system' ? systemHint() : '';
  const title = problem === 'blocked' ? BLOCKED_TITLE[device] : copy.title;
  // Camera icon when the camera is refused (alone or with the microphone);
  // microphone icon only when the camera is fine and the microphone is not.
  const icon = problem === 'blocked' && device === 'microphone' ? MicOffIcon : CameraOffIcon;

  useEffect(() => {
    trackEvent({ action: EventNames.camera_blocked_view, problem, device: problem === 'blocked' ? device : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem, device]);

  return (
    <Panel role="alertdialog" aria-labelledby="camera-blocked-title" aria-describedby="camera-blocked-body">
      {icon}
      <Title id="camera-blocked-title">{title}</Title>
      <Body id="camera-blocked-body">{copy.body}</Body>
      {hint && <Hint>{hint}</Hint>}
      <Primary
        type="button"
        onClick={() => {
          trackEvent({
            action: EventNames.camera_blocked_action,
            problem,
            device: problem === 'blocked' ? device : null,
            choice: 'retry',
          });
          onRetry();
        }}
      >
        Try again
      </Primary>
      <Secondary
        type="button"
        onClick={() => {
          trackEvent({
            action: EventNames.camera_blocked_action,
            problem,
            device: problem === 'blocked' ? device : null,
            choice: 'choose_video',
          });
          onBack();
        }}
      >
        Choose a video instead
      </Secondary>
    </Panel>
  );
};
